/* ============================================================
   SAM-656 — "Same as the business" / "Custom hours" choice for a
   service's opening hours (settings.hours_source), shown at the top of
   the Operating hours card in Scheduling & Capacity.

   With 'business' the service inherits the business's weekly Schedule
   (business.schedule_mon..sun): shown here read-only, with a link to the
   business page's Schedule section where it is edited. The service's own
   operating_hours stay in the form untouched (just hidden), so switching
   back to 'custom' restores them.

   New file under the BaseUI/Styletron freeze (SAM-175): Tailwind + tokens.
   ============================================================ */
import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Clock, ExternalLink } from 'lucide-react';
import { Notice } from '../../../components/ui/Notice';
import { DAYS_OF_WEEK } from '../../../hooks/useServiceEditor';
import type { BusinessSchedule } from '../../../utils/businessHours';
import {
  businessScheduleHref, businessScheduleToOperatingHours, formatDayHours, type HoursSource,
} from '../../../utils/serviceHoursSource';

const OPTIONS: Array<{ value: HoursSource; title: string; caption: string; icon: React.ReactNode }> = [
  {
    value: 'business',
    title: 'Same as the business',
    caption: 'Follows the business’s Schedule. Changes there apply here automatically.',
    icon: <Building2 size={14} />,
  },
  {
    value: 'custom',
    title: 'Custom hours',
    caption: 'This service has its own days and hours.',
    icon: <Clock size={14} />,
  },
];

export interface HoursSourcePickerProps {
  value: HoursSource;
  onChange: (value: HoursSource) => void;
}

export function HoursSourcePicker({ value, onChange }: HoursSourcePickerProps) {
  return (
    <div className="mb-4">
      <div role="radiogroup" aria-label="Opening hours" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className="text-left rounded-lg px-3 py-2.5 transition-colors"
              style={{
                border: `1px solid ${selected ? 'var(--tenant-primary)' : 'var(--line)'}`,
                background: selected ? 'var(--surface)' : 'var(--white)',
                boxShadow: selected ? '0 0 0 1px var(--tenant-primary)' : 'none',
              }}
            >
              <span className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: 'var(--ink-900)' }}>
                <span
                  aria-hidden
                  className="inline-block rounded-full shrink-0"
                  style={{
                    width: 14, height: 14,
                    border: `${selected ? 4 : 1.5}px solid ${selected ? 'var(--tenant-primary)' : 'var(--ink-300)'}`,
                  }}
                />
                {option.icon}
                {option.title}
              </span>
              <span className="block text-[12px] mt-0.5 pl-[22px]" style={{ color: 'var(--ink-500)' }}>
                {option.caption}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface BusinessHoursReadOnlyProps {
  businessId: string;
  /** The business row (its schedule_* columns); null while loading or when unknown. */
  business: BusinessSchedule | null;
  businessLoading?: boolean;
}

export function BusinessHoursReadOnly({ businessId, business, businessLoading }: BusinessHoursReadOnlyProps) {
  const hours = businessScheduleToOperatingHours(business);
  const hasHours = Object.keys(hours).length > 0;
  return (
        <div data-testid="business-hours-readonly">
          {businessLoading ? (
            <p className="text-[12px]" style={{ color: 'var(--ink-500)' }}>Loading the business’s schedule…</p>
          ) : (
            <>
              {!hasHours && (
                <Notice variant="warn" className="mb-2">
                  This business has no opening hours yet, so this service shows as closed every day
                  until its Schedule is set.
                </Notice>
              )}
              <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  {DAYS_OF_WEEK.map(({ key, label }) => (
                    <tr key={key} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                      <td className="py-1.5 pr-3 font-semibold uppercase text-[12px]" style={{ width: 52, color: 'var(--ink-700)' }}>
                        {label}
                      </td>
                      <td className="py-1.5" style={{ color: hours[key] ? 'var(--ink-900)' : 'var(--ink-400)' }}>
                        {formatDayHours(hours[key])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {businessId && (
                <Link
                  to={businessScheduleHref(businessId)}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold mt-2"
                  style={{ color: 'var(--tenant-primary)' }}
                >
                  Edit in the business’s Schedule <ExternalLink size={12} />
                </Link>
              )}
              <p className="text-[11px] mt-1" style={{ color: 'var(--ink-400)' }}>
                Business closures and special days apply on top. This service’s own hours are kept
                and come back if you switch to Custom hours.
              </p>
            </>
          )}
        </div>
  );
}
